/**
 * App entry (index.html → here): the boot sequence of architecture §2.2 / ui-ux §6.1.
 *
 * 1. URL params (§8.3); `window.__marg` early when dev or `?debug=1` (errors are collected from here on).
 * 2. Shell styles, device settings → `<html>` attributes; the boot page is already painted by index.html.
 * 3. Save: load pipeline (persistent) or an in-memory session (`?save=memory|fresh`, `?fixture=`, `?seed=`); the tab lock.
 * 4. `onBoot` of every feature (services, sinks, offline catch-up) — while the GPU step runs in parallel.
 * 5. `createRenderer` (F2) or lite mode «Без миниатюр».
 * 6. Fonts (≤ 1.5 s), router to the initial screen, the loop; `__marg.ready()` after the first presented frame.
 *
 * Initial screen: the hash (`#/…`, `#duel=`) → `?screen=` / `view` (→ its hash route) → `?fight=` → `app/title` on a
 * fresh save (or `?title=1`) → `meta/life`.
 */
import {
  Clock, EventBus, Rng, STORAGE_KEYS, createTranslator, signal,
  type AppContext, type FixtureId, type GameEvents, type SaveState, type ScreenContext, type ScreenDef, type ScreenParams,
  type Scope, type TabId, type Viewport,
} from '../shared';
import { RU_STRINGS } from '../content/strings/ru';
import { EN_STRINGS } from '../content/strings/en';
import { button, confirmSheet, h, installKit, sheet, toast } from '../ui';
import {
  ErrorLog, PerfOverlay, installDebugApi, loadFixtureFile, parseBootParams, parseDuration, parseWall,
  type BootParams, type DebugHost, type FixtureFile,
} from '../debug';
import pkg from '../../package.json';
import { AudioScheduler } from './audio';
import { BootScreen } from './boot';
import { showFatal, showReadOnlyBanner, showSecondTab } from './fallback';
import { ALL_FEATURES, allScreens, allSlices, type UiSave } from './features';
import { FEATURE_FLAGS } from './flags';
import { Gfx } from './gfx';
import { AppInput } from './input';
import { startLiteHost } from './lite';
import { TabLock } from './lock';
import { Loop, PowerPolicy } from './loop';
import { AppRouter, type Mounted } from './router';
import { buildHash, compileRoute, findScreen } from './routes';
import {
  SaveWriter, browserStorage, createFreshState, decodeSaveCode, encodeSaveCode, loadSave, memoryStorage, newLocalId,
  newSaveSeed, normalizeState, type KeyValueStorage, type LoadResult,
} from './save';
import { createServices } from './services';
import { SettingsStore, applyRootSettings } from './settings';
import { TABS, createShell } from './shell';
import { AppStore } from './store';
import { injectAppStyles } from './styles';
import { Telemetry } from './telemetry';

const VERSION = `${(pkg as { version: string }).version}-${import.meta.env.DEV ? 'dev' : 'build'}`;
const FONT_TIMEOUT_MS = 1500;

/** CSS viewport + DPR (capped 2) + safe-area insets (probe element; `?safe=` overrides headless zeros). */
function readViewport(safe: BootParams['safe']): Viewport {
  let s = safe;
  if (!s) {
    const probe = document.createElement('div');
    probe.style.cssText = 'position:fixed;visibility:hidden;pointer-events:none;padding:env(safe-area-inset-top,0px) env(safe-area-inset-right,0px) env(safe-area-inset-bottom,0px) env(safe-area-inset-left,0px)';
    document.body.appendChild(probe);
    const cs = getComputedStyle(probe);
    s = { t: parseFloat(cs.paddingTop) || 0, r: parseFloat(cs.paddingRight) || 0, b: parseFloat(cs.paddingBottom) || 0, l: parseFloat(cs.paddingLeft) || 0 };
    probe.remove();
  }
  return { cssW: window.innerWidth, cssH: window.innerHeight, dpr: Math.min(window.devicePixelRatio || 1, 2), portrait: window.innerHeight >= window.innerWidth, safe: s };
}

/** F3's kit boot step (tokens, ornaments, sprites, material bake, art manifest, fonts <= 1.5 s); never blocks > 2.5 s. */
function kitReady(seed: string | undefined): Promise<boolean> {
  const kit = installKit({ seed }).then((st) => st.fonts === 'loaded', (e) => { console.warn('[boot] installKit failed', e); return false; });
  return Promise.race([kit, new Promise<boolean>((r) => setTimeout(() => r(false), FONT_TIMEOUT_MS + 1000))]);
}

/** A memory copy of the persisted save keys (so a debug session reads the player's save without ever writing it). */
function copyOfSaves(local: KeyValueStorage | null): KeyValueStorage {
  const init: Record<string, string> = {};
  if (local) for (const k of STORAGE_KEYS.save) { const v = local.getItem(k); if (v !== null) init[k] = v; }
  return memoryStorage(init);
}

async function boot(): Promise<void> {
  // ── 1. params, error log, the debug root ──────────────────────────────────────────────────────────────────────────
  const params = parseBootParams(new URLSearchParams(location.search));
  const errors = new ErrorLog();
  errors.install(window);
  const debugOn = import.meta.env.DEV || params.debug;
  const dbg = debugOn ? installDebugApi({ version: VERSION, errors: () => errors.errors() }) : null;
  if (params.aliases.length) console.info(`[params] aliases used: ${params.aliases.join(', ')}`);
  for (const w of params.warnings) console.warn(`[params] ${w}`);
  injectAppStyles();
  if (params.simulate === 'app-failed') throw new Error('simulated app failure (?simulate=app-failed)');

  // ── 2. settings, root attributes ─────────────────────────────────────────────────────────────────────────────────
  const root = document.documentElement;
  const local = browserStorage();
  const settings = new SettingsStore(local,
    { motion: params.motion, textScale: params.textScale, quality: params.quality, leftHanded: params.leftHanded, font: params.font === 'plain' ? 'plain' : null },
    window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const requestedLanguage = new URLSearchParams(location.search).get('lang');
  if (requestedLanguage === 'ru' || requestedLanguage === 'en') settings.set({ lang: requestedLanguage });
  const translator = createTranslator(settings.value.lang === 'en' ? EN_STRINGS : RU_STRINGS,
    { language: settings.value.lang, onMissing: (k) => console.warn(`[i18n] missing string key "${k}"`) });
  const t = translator.t;
  const bootUi = new BootScreen(t);
  applyRootSettings(root, settings.value);
  settings.signal.subscribe((v) => applyRootSettings(root, v));
  if (params.display) root.dataset.display = params.display;
  if (params.font === 'fallback') root.dataset.font = 'fallback';
  if (params.overlays.length) root.dataset.overlay = params.overlays.join(' ');
  if (params.safe) {
    // F3's tokens read --safe-override-* (ui-ux §22.1 `safe=T,R,B,L`; headless Chrome reports 0).
    for (const [k, v] of [['t', params.safe.t], ['r', params.safe.r], ['b', params.safe.b], ['l', params.safe.l]] as const) {
      root.style.setProperty(`--safe-override-${k}`, `${v}px`);
      root.style.setProperty(`--safe-${k}`, `${v}px`);
    }
  }

  const clock = new Clock({ wallSource: () => Date.now() });
  if (params.wall !== null) clock.setWallOffset(params.wall - Date.now());
  if (params.timeScale !== null) clock.timeScale = params.timeScale;
  if (params.paused) clock.paused = true;
  const fonts = kitReady(params.seed ?? undefined).then((ok) => { bootUi.step('fonts'); return ok; });

  // ── 3. save ──────────────────────────────────────────────────────────────────────────────────────────────────────
  const slices = allSlices();
  const build = VERSION;
  let memory = params.save !== 'persistent' || local === null || params.simulate === 'save-corrupt';
  let fixture: FixtureFile | null = null;
  if (params.fixture) {
    fixture = await loadFixtureFile(params.fixture);
    if (!fixture) console.warn(`[fixture] no file for "${params.fixture}"`);
    if (fixture?.wall && params.wall === null) { const w = parseWall(fixture.wall); if (w !== null) clock.setWallOffset(w - Date.now()); }
  }
  let load: LoadResult;
  const nowWall = clock.wallNow();
  if (fixture) {
    const norm = normalizeState({ ...fixture.save, seed: params.seed ?? fixture.save.seed ?? `fixture:${fixture.id}` }, slices, nowWall, build, newSaveSeed);
    load = { ...norm, source: 'fresh', fresh: fixture.id === 'fresh', corruptKeys: [] };
  } else if (params.save === 'fresh') {
    const st = createFreshState(slices, params.seed ?? newSaveSeed(), nowWall, build);
    load = { state: st, readOnlySlices: [], created: [], recreated: [], migrated: [], messages: [], source: 'fresh', fresh: true, corruptKeys: [] };
  } else {
    let storage: KeyValueStorage = memory ? copyOfSaves(local) : local!;
    if (params.simulate === 'save-corrupt') {
      const prev = storage.getItem(STORAGE_KEYS.save[0]);
      storage = memoryStorage({ [STORAGE_KEYS.save[0]]: '{"format":"marg-save","schema":1,"broken', ...(prev ? { [STORAGE_KEYS.save[1]]: prev } : {}) });
    }
    load = loadSave({ storage, slices, nowWall, build, newSeed: newSaveSeed });
    if (params.seed) load.state = { ...load.state, seed: params.seed };
  }
  for (const m of load.messages) console.warn(`[save] ${m}`);
  if (load.corruptKeys.length && !memory) errors.record(`save: unreadable copies kept under ${load.corruptKeys.join(', ')}`);
  if (params.away !== null || fixture?.away) {
    const ms = params.away ?? parseDuration(fixture!.away!) ?? 0;
    load.state = { ...load.state, lastActiveAt: clock.wallNow() - ms };
  }
  bootUi.step('save');

  let writer!: SaveWriter;
  const store = new AppStore(load.state, { nowWall: () => clock.wallNow(), onCommit: (_s, reason) => writer.schedule(reason) });
  const bus = new EventBus<GameEvents>({ stamp: () => ({ simMs: clock.simMs, presentMs: clock.presentMs }) });
  writer = new SaveWriter({
    storage: memory ? null : local,
    getState: () => store.state,
    nowWall: () => clock.wallNow(),
    onResult: (r, reason) => {
      if (r.ok) { bus.emit('app/saved', { bytes: r.bytes, reason }); if (r.warning) console.warn(`[save] ${r.warning}`); }
      else if (r.error === 'storage-full') bus.emit('ui/toast', { key: 'app.save.full', tone: 'warning' });
      else if (r.error === 'too-big' || r.error === 'unknown') errors.record(`save: write failed (${r.error}, ${r.bytes} B)`);
    },
  });
  if (load.readOnlySlices.length) writer.setReadOnly(true);
  const ui0 = store.state.slices.ui as UiSave;
  if (!ui0.account?.localId) store.update('ui', (d) => { (d as UiSave).account = { localId: newLocalId() }; }, 'account.localId');

  let lock: TabLock | null = null;
  const overlayRoot = document.getElementById('overlay')!;
  if (!memory && local) {
    lock = new TabLock(local);
    const takeOver = (): void => { lock!.acquire(true); location.reload(); };
    lock.onLost = () => { writer.setReadOnly(true); showSecondTab(overlayRoot, t, takeOver); };
    if (params.simulate === 'second-tab' || !lock.acquire()) { writer.setReadOnly(true); showSecondTab(overlayRoot, t, takeOver); }
  }

  // ── context, chrome, router ──────────────────────────────────────────────────────────────────────────────────────
  const telemetry = new Telemetry(() => clock.wallNow());
  telemetry.listen(bus);
  const { services, registered } = createServices();
  bus.on('creature/changed', () => {
    if (services.creatures.active()) store.update('ui', (d) => { (d as UiSave).ftueStep = 'done'; }, 'hero created');
  });
  let rootRng = new Rng(params.seed ?? store.state.seed);
  const viewport = signal<Viewport>(readViewport(params.safe));
  const input = new AppInput(clock);
  input.attach(window);
  const audio = new AudioScheduler(clock);
  const applyAudio = (v: typeof settings.value): void => {
    audio.setVolumes({ music: v.music * v.master, sfx: v.sfx * v.master });
    if (v.mute !== audio.isMuted) audio.toggleMute();
  };
  applyAudio(settings.value);
  const power = new PowerPolicy(params.power, () => performance.now());
  const canvas = document.getElementById('scene') as HTMLCanvasElement;
  const uiRoot = document.getElementById('ui')!;

  let router!: AppRouter;
  const remount = (reason: string): void => router.remountAll(reason);
  const gfx = new Gfx({
    canvas, params, settings, clock, overlay: overlayRoot, t, storage: local, record: (m) => errors.record(m), remount,
    viewport: () => viewport.get(),
  });
  const gpuStep = gfx.init().then(() => { bootUi.step('gpu'); bootUi.step('pipelines'); bootUi.step('bake'); bootUi.step('meshes'); });

  const ctx = {
    store, bus, clock, services, input, t, viewport, debug: debugOn,
    get router() { return router; },
    get renderer() { return gfx.renderer; },
    get rootRng() { return rootRng; },
    // F1 extras (not in the frozen AppContext; feature-detect them — see the F1 hand-off contract requests):
    settings, audio, telemetry, flags: FEATURE_FLAGS,
    get lite() { return gfx.lite; },
    /** F3's Settings screen port (`createSettingsScreen(portOf)`), built fresh so `env` is current. */
    get settingsPort() { return settingsPort(); },
  } as AppContext & Record<string, unknown>;

  const makeContext = (def: ScreenDef, p: ScreenParams, scope: Scope): ScreenContext => {
    const sc = Object.create(ctx) as ScreenContext;
    Object.defineProperties(sc, {
      params: { value: p, enumerable: true },
      rng: { value: rootRng.stream(`screen:${def.id}`), enumerable: true },
      scope: { value: scope, enumerable: true },
    });
    return sc;
  };

  const shell = createShell(uiRoot, t, (tab) => onTab(tab));
  startLiteHost(uiRoot, () => gfx.lite);
  const defaultHash = (): string => (params.title || !services.creatures.active() ? '#/title' : '#/life');
  let lastBase: Mounted | null = null;
  router = new AppRouter({
    defs: allScreens(),
    baseLayer: shell.base,
    sheetLayer: shell.sheet,
    makeContext,
    motion: () => settings.value.motion,
    onChange: (top, base, from) => {
      shell.update(top, base);
      document.title = `${t(top.def.titleKey)} · ${t('app.name')}`;
      if (base.def.tab && base.hash) shell.remember(base.def.tab, base.hash);
      if (base !== lastBase) {
        lastBase = base;
        if (gfx.renderer && !base.def.needs3d) gfx.renderer.setScene({ kind: 'none' });
      }
      power.noteInput();
      bus.emit('app/screen', { from, to: top.def.id });
    },
    onError: (m, e) => { errors.record(m); if (e) console.warn(m, e); },
    search: () => new URLSearchParams(location.search),
    fallbackHash: defaultHash,
    t,
    sound: (id) => audio.play(id),
  });

  // Toasts: F3's kit hosts them (popover top layer); every `ui/toast` notification shows one.
  bus.on('ui/toast', (e) => {
    try { toast(t(e.key, e.params), { kind: e.tone === 'warning' ? 'error' : 'info' }); } catch (err) { console.warn('[toast]', err); }
    if (e.tone !== 'warning') audio.play('notify');
  });

  const clipboard = async (text: string, okKey: string): Promise<void> => {
    try { await navigator.clipboard.writeText(text); bus.emit('ui/toast', { key: okKey, tone: 'info' }); } catch { bus.emit('ui/toast', { key: 'app.clipboard.failed', tone: 'warning' }); }
  };
  const diagnostics = (): string => JSON.stringify({
    version: VERSION, url: location.href, ua: navigator.userAgent, viewport: viewport.get(), settings: settings.value,
    platform: { lite: gfx.lite, liteReason: gfx.liteReason, recovered: gfx.recovered, storage: memory ? 'memory' : 'local', readOnly: writer.isReadOnly },
    perf: loop.stats(), errors: errors.errors(), storeLog: store.log().slice(-30), telemetry: telemetry.events(200),
  }, null, 2);
  const settingsPort = () => settings.port({
    env: {
      webgpu: !gfx.lite || gfx.liteReason === 'manual',
      standalone: window.matchMedia('(display-mode: standalone)').matches || root.dataset.display === 'standalone',
      vibrate: typeof navigator.vibrate === 'function',
      fullscreen: typeof document.documentElement.requestFullscreen === 'function' && !/iPhone|iPad/.test(navigator.userAgent),
    },
    actions: {
      exportSave: () => clipboard(encodeSaveCode(store.state, clock.wallNow()), 'app.save.copied'),
      importSave: () => {
        if (writer.isReadOnly) { bus.emit('ui/toast', { key: 'app.save.readonly.title', tone: 'warning' }); return; }
        const input = h('textarea', { attrs: { rows: 6, 'aria-label': t('app.save.import.code'), spellcheck: false },
          style: 'display:block;width:100%;box-sizing:border-box;min-height:130px;font:14px/1.4 monospace;padding:12px;border:1px solid #a68b5c;background:#fff8e9;color:#342719' });
        const error = h('p', { attrs: { role: 'status' } });
        const dialog = sheet({ title: t('app.save.import.title'), body: [h('p', null, t('app.save.import.hint')), input, error],
          actions: button({ label: t('app.save.import.check'), variant: 'primary', onClick: async () => {
            const decoded = decodeSaveCode(input.value.trim());
            if (!decoded.ok) { error.textContent = t('app.save.import.invalid'); return; }
            const norm = normalizeState(decoded.envelope.data, slices, clock.wallNow(), build, newSaveSeed);
            if (norm.readOnlySlices.length || norm.recreated.length) { error.textContent = t('app.save.import.invalid'); return; }
            await dialog.close();
            confirmSheet({ title: t('app.save.import.confirm'), consequence: t('app.save.import.replace'), verb: t('ui.settings.importSave'),
              onConfirm: () => {
                store.replace(norm.state, 'import save');
                writer.flush('import save');
                rootRng = new Rng(norm.state.seed);
                void router.goto(services.creatures.active() ? '#/life' : '#/title', {}, { replace: true });
                bus.emit('ui/toast', { key: 'app.save.import.done', tone: 'reward' });
              },
            }).open();
          } }),
        });
        dialog.open();
      },
      copyDiagnostics: () => clipboard(diagnostics(), 'app.diag.copied'),
      resetProgress: () => {
        if (writer.isReadOnly) { bus.emit('ui/toast', { key: 'app.save.readonly.title', tone: 'warning' }); return; }
        const st = createFreshState(slices, newSaveSeed(), clock.wallNow(), build);
        store.replace(st, 'reset progress');
        store.update('ui', (d) => { (d as UiSave).account = { localId: newLocalId() }; }, 'account.localId');
        writer.flush('reset');
        rootRng = new Rng(st.seed);
        void router.goto('#/title', {}, { replace: true });
      },
    },
  });
  // «Без миниатюр» from Settings (persisted): switch now; turning it off re-runs the GPU step.
  const initialLanguage = settings.value.lang;
  const initialQuality = settings.value.quality;
  settings.signal.subscribe((v) => {
    if (v.lang === initialLanguage && v.quality === initialQuality) return;
    writer.flush('language or graphics change');
    const nextUrl = new URL(location.href);
    nextUrl.searchParams.delete('lang');
    location.replace(nextUrl.href);
  });
  let liteSetting = settings.value.lite;
  settings.signal.subscribe((v) => {
    if (v.lite === liteSetting) return;
    liteSetting = v.lite;
    if (v.lite && !gfx.lite) gfx.goLite();
    else if (!v.lite && gfx.lite && gfx.liteReason === 'manual') void gfx.retry();
  });

  const onTab = (tab: TabId): void => {
    const top = router.top;
    const base = router.base;
    const target = shell.tabTarget(tab, base?.hash ?? null, base?.def.tab);
    if ('scrollTop' in target) {
      if (top && top.layer !== 'base') { void router.goto(base?.hash ?? TABS.find((x) => x.tab === tab)!.hash); return; }
      base?.el.scrollTo({ top: 0, behavior: settings.value.motion === 'full' ? 'smooth' : 'auto' });
      return;
    }
    void router.goto(target.hash);
  };

  // Input wiring: back → router (screens answer through onBack), M → mute, 1–5 → tabs outside immersive screens.
  input.on('back', () => { if (!document.querySelector('dialog[open]')) router.back(); });
  input.onAny = (e) => { power.noteInput(); if (e.type === 'pointerdown' || e.type === 'keydown') audio.unlock(); };
  window.addEventListener('keydown', (e) => {
    if (e.ctrlKey || e.metaKey || e.altKey || e.defaultPrevented) return;
    const tgt = e.target as HTMLElement | null;
    if (tgt && (/^(INPUT|TEXTAREA|SELECT)$/.test(tgt.tagName) || tgt.isContentEditable)) return;
    if (e.key === 'm' || e.key === 'M' || e.key === 'ь' || e.key === 'Ь') { settings.set({ mute: !audio.isMuted }); return; }
    const n = Number(e.key);
    const kind = router.base?.def.kind;
    if (n >= 1 && n <= 5 && kind !== 'immersive' && kind !== 'stage' && kind !== 'debug') onTab(TABS[n - 1].tab);
  });

  // ── the loop ─────────────────────────────────────────────────────────────────────────────────────────────────────
  const reported = new Set<string>();
  const safely = (id: string, what: string, fn: () => void): void => {
    try { fn(); } catch (e) {
      const key = `${id}:${what}`;
      if (!reported.has(key)) { reported.add(key); errors.record(`screen ${id}: ${what} threw: ${(e as Error)?.message ?? String(e)}`); console.warn(e); }
    }
  };
  let frames = 0;
  let firstFrame: (() => void) | null = null;
  const firstFramePromise = new Promise<void>((r) => { firstFrame = r; });
  const presentationCap=():number|null=>{
    const idle=power.cap(router.base?.def.kind);
    const ceiling=settings.value.fps===30?30:null;
    return idle===null?ceiling:ceiling===null?idle:Math.min(idle,ceiling);
  };
  const loop = new Loop(clock, {
    tick(dt) { for (const m of router.mounted()) if (m.screen.update) safely(m.def.id, 'update', () => m.screen.update!(dt)); },
    render(presentMs, realDt) {
      for (const m of router.mounted()) if (m.screen.render) safely(m.def.id, 'render', () => m.screen.render!(presentMs, realDt));
      const r = gfx.renderer;
      if (r) {
        const base = router.base;
        try {
          if (base && base.def.needs3d && !base.screen.render) r.advanceTo({ presentMs, worldMs: presentMs, vfxMs: presentMs });
          if (r.isAnimating() || r.frameRequested) r.frame(presentationCap());
        } catch (e) {
          if (!reported.has('renderer')) { reported.add('renderer'); errors.record(`renderer: frame threw: ${(e as Error)?.message ?? String(e)}`); }
        }
      }
    },
    endFrame(ts, presentMs) {
      input.markFrame(ts, presentMs);
      input.endFrame();
      audio.markFrame(presentMs);
      frames++;
      if (firstFrame) { const f = firstFrame; firstFrame = null; f(); }
    },
  }, {
    powerCap: presentationCap,
  });
  if (params.cadence !== null) loop.setCadence(params.cadence, params.jitter, params.seed ?? 'cadence');

  // ── 4. features (GPU step runs in parallel) ──────────────────────────────────────────────────────────────────────
  for (const f of ALL_FEATURES) {
    try { await f.onBoot?.(ctx); } catch (e) { errors.record(`feature ${f.key}: onBoot threw: ${(e as Error)?.message ?? String(e)}`); console.warn(e); }
  }
  await gpuStep;

  // ── debug host + namespaces ──────────────────────────────────────────────────────────────────────────────────────
  let ready = false;
  let hiddenUi = false;
  const toMemory = (): void => { if (!memory) { memory = true; writer.toMemory(); lock?.release(); } };
  const replaceState = (raw: unknown, reason: string): void => {
    const norm = normalizeState(raw, slices, clock.wallNow(), build, newSaveSeed);
    toMemory();
    store.replace(norm.state, reason);
    rootRng = new Rng(params.seed ?? norm.state.seed);
    router.remountAll(reason);
  };
  const perf = new PerfOverlay(() => {
    const s = loop.stats();
    const rs = gfx.renderer?.stats;
    return {
      fps: s.fps, cpuMsAvg: s.cpuMsAvg, cpuMsP95: s.cpuMsP95, slowFrames: s.slowFrames, presentMs: clock.presentMs, simMs: clock.simMs,
      paused: clock.paused, timeScale: clock.timeScale, cadenceHz: s.cadenceHz, screen: router.current?.id ?? null, lite: gfx.lite,
      errors: errors.count, draws: rs?.drawCalls, tris: rs?.triangles, gpuMs: rs?.gpuMs,
    };
  });
  if (params.overlays.includes('perf')) perf.show();
  if (debugOn) input.onDebugKey = (k) => { if (k === 'F2') perf.toggle(); };

  const host: DebugHost = {
    version: VERSION,
    clock,
    isReady: () => ready,
    isIdle: () => router.isIdle() && !document.getElementById('boot'),
    frames: () => frames,
    renderNow: () => loop.frame(performance.now()),
    setCadence: (hz, jitter = 0) => loop.setCadence(hz, jitter, params.seed ?? 'cadence'),
    cadence: () => loop.stats().cadenceHz,
    timeTravel: (ms) => {
      clock.setWallOffset(clock.wallOffsetMs + ms);
      bus.emit('app/visibility', { hidden: false });
    },
    goto: (screen, p) => router.goto(screen, p as ScreenParams | undefined),
    back: () => router.back(),
    screens: () => router.list(),
    screenState: () => {
      const top = router.top;
      return top ? { id: top.def.id, params: top.params, debug: top.screen.debugState?.() ?? null } : null;
    },
    save: () => store.state as SaveState,
    seed: () => rootRng.key,
    storeLog: () => store.log(),
    viewport: () => viewport.get(),
    setSave: (s) => {
      if (typeof s === 'string') {
        const d = decodeSaveCode(s);
        if (!d.ok) throw new Error(`__marg.setSave: ${d.error}`);
        replaceState(d.envelope.data, 'debug setSave');
      } else replaceState(s, 'debug setSave');
    },
    loadFixture: async (id: FixtureId) => {
      const fx = await loadFixtureFile(id);
      if (!fx) throw new Error(`__marg.loadFixture: no fixture "${id}"`);
      if (fx.wall) { const w = parseWall(fx.wall); if (w !== null) clock.setWallOffset(w - Date.now()); }
      const st = { ...fx.save, seed: fx.save.seed ?? `fixture:${id}` } as Record<string, unknown>;
      if (fx.away) { const ms = parseDuration(fx.away); if (ms !== null) st.lastActiveAt = clock.wallNow() - ms; }
      replaceState(st, `fixture ${id}`);
    },
    exportSave: () => encodeSaveCode(store.state, clock.wallNow()),
    setSeed: (seed) => { rootRng = new Rng(seed); router.remountAll('setSeed'); },
    events: (n) => bus.events(n),
    perf: () => {
      const s = loop.stats();
      const mem = (performance as Performance & { memory?: { usedJSHeapSize: number } }).memory;
      return { fps: s.fps, cpuMsAvg: s.cpuMsAvg, cpuMsP95: s.cpuMsP95, gpuMs: gfx.renderer?.stats.gpuMs ?? null, slowFrames: s.slowFrames, heapMB: mem ? mem.usedJSHeapSize / 1048576 : null };
    },
    renderStats: () => gfx.renderer?.stats ?? null,
    loseDevice: () => gfx.loseDevice(),
    setQuality: (tier) => { void gfx.setQuality(tier); },
    hideUi: (hidden) => {
      hiddenUi = hidden;
      for (const el of [uiRoot, overlayRoot]) { if (hidden) el.dataset.hiddenUi = '1'; else delete el.dataset.hiddenUi; }
    },
    platform: () => ({
      version: VERSION, lite: gfx.lite, liteReason: gfx.liteReason, gpuRecovered: gfx.recovered, storage: memory ? 'memory' : 'local',
      readOnly: writer.isReadOnly, saves: writer.writes, services: registered(), hiddenUi, loadSource: load.source, fresh: load.fresh,
      flags: FEATURE_FLAGS, params,
    }),
  };
  if (dbg) {
    dbg.attach(host);
    const ns: Record<string, Record<string, unknown>> = {
      telemetry: { events: (n?: number) => telemetry.events(n), export: () => telemetry.export(), track: (name: string, data?: unknown) => telemetry.track(name, data) },
      render: { audioLog: () => audio.log.slice() },
    };
    for (const f of ALL_FEATURES) {
      try {
        for (const [name, members] of Object.entries(f.debugApi?.(ctx) ?? {})) ns[name] = { ...(ns[name] ?? {}), ...members };
      } catch (e) { errors.record(`feature ${f.key}: debugApi threw: ${(e as Error)?.message ?? String(e)}`); }
    }
    dbg.mount(ns);
  }

  // ── 6. fonts, initial route, loop ────────────────────────────────────────────────────────────────────────────────
  await fonts;
  const initial = initialTarget(params, location.hash, defaultHash());
  loop.start();
  if (initial.kind === 'def') await router.startOn(initial.def);
  else await router.start(initial.hash);
  if (params.t !== null) { clock.step(params.t); loop.frame(performance.now()); }
  await firstFramePromise;
  bootUi.step('frame');
  ready = true;
  root.dataset.shell = 'ready';
  if (params.hideUi) host.hideUi(true);
  void bootUi.exit(settings.value.motion);

  // ── after boot: notices, persistence triggers, viewport ──────────────────────────────────────────────────────────
  gfx.showBannerIfNeeded();
  if (load.readOnlySlices.length) showReadOnlyBanner(overlayRoot, t);
  else if (load.source === 'save.1' || load.source === 'save.2') bus.emit('ui/toast', { key: 'app.save.restored', tone: 'warning' });
  if (params.simulate === 'device-lost') setTimeout(() => gfx.loseDevice(), 1200);

  document.addEventListener('visibilitychange', () => {
    const hidden = document.visibilityState === 'hidden';
    if (hidden) { store.touchActive(clock.wallNow()); writer.flush('hidden'); }
    else loop.resetTiming();
    bus.emit('app/visibility', { hidden });
  });
  window.addEventListener('pagehide', () => { store.touchActive(clock.wallNow()); writer.flush('pagehide'); lock?.release(); });
  const onResize = (): void => {
    const vp = readViewport(params.safe);
    viewport.set(vp);
    try { gfx.renderer?.resize(vp); } catch (e) { errors.record(`renderer: resize threw: ${(e as Error)?.message ?? String(e)}`); }
    router.resize(vp);
  };
  window.addEventListener('resize', onResize);
  window.visualViewport?.addEventListener('resize', onResize);
  settings.signal.subscribe(applyAudio);
}

/** Architecture §2.2 initial screen order (+ ui-ux §3.3 `?fight=` compatibility). */
function initialTarget(params: BootParams, hash: string, fallback: string): { kind: 'hash'; hash: string } | { kind: 'def'; def: ScreenDef } {
  if (hash && hash !== '#' && hash !== '#/') return { kind: 'hash', hash };
  const defs = allScreens();
  if (params.screen) {
    const def = findScreen(defs, params.screen);
    if (!def) console.warn(`[router] ?screen=${params.screen}: unknown screen`);
    else if (!def.route) return { kind: 'def', def };
    else {
      const q = new URLSearchParams(location.search);
      const names = compileRoute(def.route).names;
      const p: ScreenParams = {};
      for (const n of names) { const v = q.get(n) ?? (n === 'fightId' ? params.fight : null); if (v) p[n] = v; }
      try { return { kind: 'hash', hash: buildHash(def.route, p) }; } catch {
        for (const n of names) if (p[n] === undefined) p[n] = 'demo';
        try { return { kind: 'hash', hash: buildHash(def.route, p) }; } catch { /* fall through */ }
      }
    }
  }
  if (params.fight) return { kind: 'hash', hash: `#/arena/fight/${encodeURIComponent(params.fight)}` };
  return { kind: 'hash', hash: fallback };
}

boot().catch((e: unknown) => {
  console.error('[boot] failed', e);
  document.getElementById('boot')?.remove();
  injectAppStyles();
  const t = createTranslator(RU_STRINGS).t;
  showFatal(t, () => JSON.stringify({ error: String((e as Error)?.stack ?? e), url: location.href, ua: navigator.userAgent, errors: window.__marg?.errors?.() ?? [] }, null, 2));
});
